<script lang="ts" setup>
import CommunityButton from '@/components/common/buttons/CommunityButton.vue'
import { GroupState } from '@/composition/groups'
import { Theme } from '@/composition/themes'
import { GroupType, MemberStatus } from '@/models/Groups'
import {
  Lock02Icon,
  Settings01Icon,
  Users01Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UISkeleton,
  UITextMdMedium,
  UITextXsRegular,
} from '@gohighlevel/ghl-ui'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import draggable from 'vuedraggable-es'
import { truncateString } from '../../helper'
import ChannelIcon from '../ChannelIcon.vue'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()

const hoveredChannelSlug: any = ref(null)
const isLoading = ref(true)
const channelList = computed(() => {
  if (GroupState.state?.userStatus !== MemberStatus.BANNED) {
    return GroupState.state.channelList
  } else {
    return []
  }
})

const emit = defineEmits([
  'channel-changed',
  'close-sidenav',
  'open-modal-flag',
  'show-add-channel-modal',
])
const pagesize = 30

onMounted(async () => {
  await registerObserver()
})
async function registerObserver() {
  nextTick(async () => {
    const observer = new IntersectionObserver(
      async entries => {
        if (entries[0].isIntersecting && !isLoading.value) {
          isLoading.value = true
          await GroupState.getGroupByLocation({ limit: pagesize })
          isLoading.value = false
        }
      },
      {
        threshold: 1.0,
      }
    )
    const target = document.getElementById('observe-list-end')
    if (target) {
      observer.observe(target)
    }
  })
}

function homeTimeline() {
  router.push({
    name: 'home',
    params: { groupSlug: route.params.groupSlug },
  })
}

// draggable channels
const isDragActive = ref(false)
const activeDragIndex = ref(null)
const isGroupAdmin = computed(() => GroupState.state.isGroupAdmin)
async function elementUpdated(event: any) {
  const { oldIndex, newIndex } = event
  if (oldIndex != newIndex) {
    // rearrange the channel from its old index to new index and update the sequence
    channelList.value.splice(
      newIndex,
      0,
      channelList.value.splice(oldIndex, 1)[0]
    )
    await GroupState.rearrangeChannels(
      channelList.value.map((ch, index) => {
        return { id: ch._id, channelName: ch.name, sequence: index + 1 }
      })
    )
  }
}

const dragOptions = computed(() => ({
  animation: 250,
  disabled: isGroupAdmin.value ? false : true,
  fallbackOnBody: true,
  swapThreshold: 0.8,
  chosenClass: 'chosen-channel',
  scroll: true,
  forceAutoScrollFallback: true,
}))

function startDrag(event) {
  isDragActive.value = true
  activeDragIndex.value = event.oldIndex
}
function endDrag() {
  isDragActive.value = false
  activeDragIndex.value = null
}

defineProps({
  isLoadingMetadata: {
    type: Boolean,
    default: false,
  },
})
</script>
<template>
  <div id="side-bar h-container">
    <div class="relative grid h-container">
      <div
        v-if="
          GroupState.state?.groupType === GroupType.PUBLIC ||
          GroupState.isActiveMember()
        "
        class="container h-container"
      >
        <div
          class="mx-4 box-border max-w-[263px] border border-l-0 border-r-0 border-t-0 border-solid border-communities-sidebar-border py-4"
        >
          <div
            id="home-timeline"
            class="box-border grid w-full cursor-pointer grid-cols-6 items-center rounded-md px-3 py-3 xl:grid-cols-8"
            :class="
              route.name === 'home'
                ? 'bg-communities-primary text-communities-sidebar-selected-item-font'
                : 'hover:bg-communities-sidebar-fill'
            "
            tabindex="0"
            role="button"
            aria-label="Home"
            @click="homeTimeline"
            @keyup.enter="homeTimeline"
          >
            <Users01Icon
              class="col-span-1 mr-3 h-5 w-5"
              :class="
                route.name === 'home'
                  ? 'text-communities-sidebar-selected-item-font'
                  : 'text-communities-sidebar-font-primary'
              "
            />
            <UITextMdMedium
              class="col-span-4 truncate xl:col-span-6"
              :class="
                route.name === 'home'
                  ? 'text-communities-sidebar-selected-item-font'
                  : 'text-communities-sidebar-font-primary'
              "
              >{{ t('common.home') }}</UITextMdMedium
            >
          </div>
        </div>
        <div
          class="channel-list-scrollbar channel-list revert-box-sizing overflow-y-auto px-4"
        >
          <draggable
            v-if="channelList != undefined"
            v-model="channelList"
            class="content"
            v-bind="dragOptions"
            item-key="order"
            @update="elementUpdated"
            @start="startDrag"
            @end="endDrag"
          >
            <template #item="{ element }">
              <div
                :id="`${element.slug}-sidebar-item`"
                :key="element.slug"
                class="group mt-2 flex h-7 cursor-pointer items-center justify-between rounded p-[10px]"
                :class="
                  route.params.channelSlug == element.slug
                    ? ' bg-communities-primary text-communities-sidebar-selected-item-font'
                    : 'hover:bg-communities-sidebar-fill'
                "
                tabindex="0"
                role="menuitem"
                :aria-label="`Switch to ${element.name} channel`"
                :aria-current="
                  route.params.channelSlug === element.slug ? 'page' : undefined
                "
                @click="emit('channel-changed', element.slug)"
                @keyup.enter="emit('channel-changed', element.slug)"
                @mouseenter="hoveredChannelSlug = element.slug"
                @mouseleave="hoveredChannelSlug = null"
              >
                <div
                  class="grid w-full grid-cols-6 items-center font-medium xl:grid-cols-8"
                >
                  <ChannelIcon
                    :channel="element"
                    :showing-in-sidebar="true"
                    :lock-icon-fill="
                      Theme.state.isDarkMode
                        ? 'var(--background)'
                        : 'var(--fontPrimary)'
                    "
                    :lock-icon-css="
                      Theme.state.isDarkMode
                        ? 'rounded-full bg-communities-font-primary'
                        : 'rounded-full bg-communities-background border border-solid border-communities-border'
                    "
                  />
                  <UITextMdMedium
                    :id="`${element.name}-channel-name`"
                    class="text-md col-span-4 truncate xl:col-span-6"
                    :class="[
                      route.params.channelSlug == element.slug
                        ? 'text-communities-sidebar-selected-item-font'
                        : 'text-communities-sidebar-font-primary',
                    ]"
                    >{{ truncateString(element.name, 25) }}</UITextMdMedium
                  >
                  <div class="flex items-center gap-2">
                    <div
                      v-if="
                        element.unreadCount > 0 &&
                        !(
                          route.params.channelSlug === element.slug &&
                          element.slug === hoveredChannelSlug
                        )
                      "
                      :id="`${element._id}-unread-count`"
                      class="flex h-[20px] w-7 items-center justify-center rounded-full bg-communities-primary"
                    >
                      <span
                        class="text-xs font-medium text-communities-sidebar-selected-item-font"
                      >
                        {{
                          element.unreadCount > 98 ? '99+' : element.unreadCount
                        }}
                      </span>
                    </div>
                    <button
                      v-if="
                        route.params.channelSlug === element.slug &&
                        element.slug === hoveredChannelSlug
                      "
                      id="edit_channel_btn"
                      class="order-last col-span-1 flex cursor-pointer items-center justify-center rounded-full border-none bg-transparent"
                      :class="
                        route.params.channelSlug == element.slug
                          ? 'text-communities-sidebar-selected-item-font'
                          : 'text-communities-sidebar-font-primary'
                      "
                      @click="emit('open-modal-flag')"
                    >
                      <Settings01Icon
                        id="edit_channel_btn_icon"
                        class="h-5 w-5"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </draggable>
          <!-- repeat this component for 5 times -->
          <div v-else>
            <div
              v-for="index in 6"
              :key="index"
              class="mt-2 flex h-7 cursor-pointer items-center justify-between rounded p-[10px]"
            >
              <div
                class="grid w-full grid-cols-6 items-center font-medium xl:grid-cols-8"
              >
                <UISkeleton height="24px" class="mr-2" circle />
                <UISkeleton
                  class="col-span-4 xl:col-span-6"
                  :round="true"
                  height="24px"
                  width="full"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="GroupState.state.isGroupAdmin"
          class="bottom-0 left-0 row-span-1 box-border flex h-[80px] w-full items-center justify-center border-[0.5px] border-b-0 border-l-0 border-r-0 border-solid border-communities-sidebar-border px-4"
        >
          <CommunityButton
            id="add-channel__btn"
            button-type="primary"
            class="revert-box-sizing my-4 flex h-10 w-full cursor-pointer items-center justify-center border-1"
            @click="emit('show-add-channel-modal')"
          >
            <div
              class="flex items-center justify-center gap-2 font-medium uppercase text-communities-sidebar-selected-item-font"
            >
              + {{ t('channel.addChannel') }}
            </div>
          </CommunityButton>
        </div>
      </div>
      <div
        v-else
        class="flex h-full flex-col items-center justify-center text-communities-sidebar-font-secondary"
      >
        <Lock02Icon class="h-8 w-8" />
        <UITextMdMedium class="mt-3">{{
          t('groupInfo.groupDetails.discovery.discoverChannels')
        }}</UITextMdMedium>

        <UITextXsRegular class="mt-3">{{
          t('groupInfo.groupDetails.discovery.joinGroupToExploreAllChannels')
        }}</UITextXsRegular>
      </div>
    </div>
  </div>
</template>

<style scoped>
.container {
  display: grid;
  grid-template-rows: auto 1fr auto;
}
.radio-style {
  width: 200px;
  max-width: 300px;
}

#radio-style:checked {
  background-color: #3b82f6;
}

.channel-list-scrollbar::-webkit-scrollbar {
  width: 0px;
}

.channel-list-scrollbar:hover::-webkit-scrollbar {
  width: 4px;
}

.channel-list-scrollbar::-webkit-scrollbar-thumb {
  background-color: var(--fontSecondary);
  border-radius: 2px;
}

#side-bar::-webkit-scrollbar {
  width: 0px;
}
.revert-box-sizing {
  box-sizing: revert !important;
}
.n-divider:not(.n-divider--vertical) {
  margin-top: 10px;
  margin-bottom: 10px;
}
.chosen-channel {
  border-bottom: 3px solid green;
}

@media (max-width: 930px) {
  .channel-list {
    height: 100% !important;
  }
}
</style>
